// Evidence State & Confidence Classifier
//
// A PURE classification layer over A1's evidence nodes (evidenceNodes.js) and
// over the engine's two auto-evidence surfaces (autoEvidence.js CHECKERS and
// evidenceDerivations.js DERIVATION_MAP). It answers two questions about every
// piece of evidence, without re-deriving any of it:
//
//   1. state       — what KIND of proof is this?
//        observed  = a recorded fact read directly (published doc, acknowledgement,
//                    device state, merged PR)
//        inferred  = derived by correlation / arithmetic (coverage %, a cadence
//                    or deadline lapse, "no critical vulns")
//        confirm   = a judgment no data source captures (destined for a one-tap
//                    attestation later)
//        upload    = a genuine external artefact (signed contract, certificate,
//                    outside report)
//
//   2. confidence  — how much can the auditor narrative lean on it?
//        high / medium / low, each with a short `confidenceReason`.
//
// The honesty rule (non-negotiable): every inferred / proxy node MUST carry its
// confidence and reason, and a check whose integration is not connected MUST
// surface as "unprovable — integration not connected", never as a silent pass —
// exactly the way the malware-detection derivation already hedges.
//
// Classification signals we encode:
//   • A direct integration/device/record fact (mfaEnabled, encryptionAtRest, an
//     approved document, a verified completion record) → observed / high.
//   • A proxy — one signal standing in for another (Defender-AV inferred from
//     Intune complianceState) → inferred / low.
//   • A time-driven outcome (SLA, cadence, deadline, freshness, expiry) → a
//     lapse/currency derivation → inferred / medium.
//   • A `pass:null` derivation, or a checker reporting its integration is not
//     connected → low + "unprovable — integration not connected".
//
// Pure and read-time: no state keys, no persistence, no side effects.
//
// Dependency direction is one-way: evidenceNodes.js depends on this module, not
// the reverse. To avoid an import cycle, the node-kind keys below are the
// literal EVIDENCE_NODE_KINDS values (kept in sync with evidenceNodes.js) rather
// than an import of that symbol.

export const EVIDENCE_STATE = Object.freeze({
  OBSERVED: 'observed',
  INFERRED: 'inferred',
  CONFIRM: 'confirm',
  UPLOAD: 'upload',
});

export const CONFIDENCE = Object.freeze({
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
});

// The canonical hedge for anything that cannot be proven because the data
// source behind it is not connected. Kept as one string so callers and tests
// can assert on it exactly.
export const UNPROVABLE_REASON = 'Unprovable — integration not connected';

// GH #6428. `pass: null` has two distinct causes and they owe the tenant
// different sentences. A disconnected feed is fixed by connecting it; a
// register nobody has filled in is fixed by filling it in, and there is no
// integration to connect. Reusing the string above for the second case names
// a remedy the product does not offer — and §10's 2026-09-07 addendum holds a
// refusal's stated reason to the same standard as a promise.
//
// A checker signals the second cause by setting `details.notRecorded`.
export const NOT_RECORDED_REASON = 'Unprovable — the record has not been created';

const c = (state, confidence, confidenceReason) =>
  Object.freeze({ state, confidence, confidenceReason });

// ── A1 evidence-node kinds ────────────────────────────────────────────────────
// The five concrete evidence kinds the 6-gate engine surfaces today. These are
// what selectEvidenceNodes() emits; classifyNode() attaches the tag to each.
const NODE_KIND_CLASSIFICATION = Object.freeze({
  // 'approved-document' — a published, approved document read straight from the register.
  'approved-document': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Published, approved document read directly from the document register.'
  ),
  // 'verified-artefact' — a verified external artefact: the upload channel
  // (signed contract, certificate, attestation, outside report) anchored to the control.
  'verified-artefact': c(
    EVIDENCE_STATE.UPLOAD,
    CONFIDENCE.HIGH,
    'External artefact uploaded and verified against the control.'
  ),
  // 'completed-risk-action' — a recorded, completed risk-treatment action.
  'completed-risk-action': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Risk treatment action recorded as completed.'
  ),
  // 'satisfied-risk-obligation' — recurring verification work; its currency is
  // derived from the last-satisfied date against the obligation cadence + grace
  // window (a lapse derivation).
  'satisfied-risk-obligation': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Currency derived from the last-satisfied date against the obligation cadence window.'
  ),
  // 'fulfilled-obligation' — a catalogue obligation whose fulfilment is a rollup
  // derived from its underlying evidence (OBL-B-002); the node level cannot see
  // which source produced it.
  'fulfilled-obligation': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Obligation fulfilment is derived from its underlying evidence.'
  ),
});

// Hedged fallback for an unrecognised node kind — never over-claim observed/high.
const NODE_DEFAULT = c(
  EVIDENCE_STATE.INFERRED,
  CONFIDENCE.MEDIUM,
  'Unclassified evidence kind — treated as inferred pending explicit classification.'
);

// ── evidenceDerivations.js DERIVATION_MAP (the 7 tri-state entries) ───────────
// The only surface with the tri-state pass:null ("cannot determine — integration
// not connected"). classifyDerivation() promotes a pass:null result to the
// unprovable hedge regardless of the entry's base classification.
const DERIVATION_CLASSIFICATION = Object.freeze({
  // Direct integration facts — read straight off the synced record.
  'mfa-all-users': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Direct integration fact: each account’s MFA state read from Entra ID.'
  ),
  'disk-encryption-all-endpoints': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Direct integration fact: device encryption state read from Intune.'
  ),
  'asset-owners-assigned': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Direct fact: owner field read from the asset registry.'
  ),
  'security-training-completed': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Direct fact: verified training-completion records read from the evidence store.'
  ),
  // Proxy — one signal standing in for another. The malware checker infers
  // Defender antivirus from Intune device compliance; it is never read directly.
  'malware-detection-all-endpoints': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.LOW,
    'Proxy: Defender antivirus inferred from Intune device compliance, not read directly.'
  ),
  // Absence-of-evidence claims correlated over the Defender feed.
  'no-critical-vulnerabilities': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Derived from the correlated Defender feed — a clean feed is absence of findings, not proof of none.'
  ),
  'no-high-vulnerabilities': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Derived from the correlated Defender feed — a clean feed is absence of findings, not proof of none.'
  ),
});

const DERIVATION_DEFAULT = c(
  EVIDENCE_STATE.INFERRED,
  CONFIDENCE.MEDIUM,
  'Unclassified derivation — treated as inferred pending explicit classification.'
);

// ── autoEvidence.js CHECKERS (~44) ────────────────────────────────────────────
// Classification rule applied consistently:
//   • Reads a recorded field / status / verified record directly → observed/high.
//   • Outcome depends on the current time (SLA, cadence, deadline, freshness,
//     expiry) → a lapse/currency derivation → inferred/medium.
//   • One signal stands in for another (proxy) → inferred/low.
//   • Boolean derived from an external audit/feed → inferred/medium, and
//     unprovable when that integration is not connected.
const CHECKER_CLASSIFICATION = Object.freeze({
  // Documents
  'document-published': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads document publication status directly.'
  ),
  'document-approved': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads document approval decision directly.'
  ),
  'document-acknowledged': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded reader acknowledgements directly.'
  ),
  'document-within-sla': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Review-SLA currency derived from next-review date against now.'
  ),
  // Risk
  'risk-register-exists': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads the risk register directly.'
  ),
  'risks-reviewed': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded risk status directly.'
  ),
  'risks-approved': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded risk resolution status directly.'
  ),
  // Vendor
  'vendor-assessed': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded vendor assessment answers directly.'
  ),
  'vendor-assessment-current': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Assessment currency derived from next-assessment date against now.'
  ),
  'vendor-contracts-tracked': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded contract end dates directly.'
  ),
  'subprocessor-registry-maintained': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded processor registry fields directly.'
  ),
  'subprocessor-dpa-signed': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'DPA currency derived from signed status and expiry date against today.'
  ),
  'subprocessor-oversight-current': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Oversight currency derived from last/next assessment dates against a 12-month window.'
  ),
  'transfer-basis-documented': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded transfer mechanism field directly.'
  ),
  // Employees / JML
  'employees-screened': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads the screening checklist item across every active employee, counting those with no joiner record as unevidenced.'
  ),
  'employees-nda-signed': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads the NDA checklist item across every active employee, counting those with no joiner record as unevidenced.'
  ),
  'employees-training': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads the security-training checklist item across every active employee, counting those with no joiner record as unevidenced.'
  ),
  'employees-access-revoked': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded leaver access-revocation checklist item directly.'
  ),
  'employees-assets-returned': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded leaver asset-return checklist item directly.'
  ),
  'employees-evaluated': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded evaluation records directly.'
  ),
  'evidence-requirement-met': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads verified evidence records directly (validity filtered by expiry).'
  ),
  'assets-returned-by-leavers': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded asset assignment and return state directly.'
  ),
  'roles-acknowledged': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded role acknowledgements directly.'
  ),
  'management-body-trained': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded management-body training and attestation records directly.'
  ),
  'entra-stale-accounts-clear': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Staleness derived from last sign-in date against a threshold.'
  ),
  'privileged-mfa-enrolled': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Direct integration fact: privileged-account MFA state read from Entra ID.'
  ),
  // Assets & system
  'asset-inventory-exists': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads the asset registry directly.'
  ),
  'asset-owners-assigned': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded asset owner assignments directly.'
  ),
  'asset-classification-complete': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded asset data classifications directly.'
  ),
  'activity-log-configured': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded activity-log retention setting directly.'
  ),
  'jml-process-complete': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded JML process status directly.'
  ),
  // Privacy / GDPR
  'ropa-entries-exist': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads the RoPA register directly.'
  ),
  'ropa-lawful-basis-complete': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded lawful-basis fields directly.'
  ),
  'ropa-transfers-documented': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded transfer mechanisms directly.'
  ),
  'dpia-approved-exists': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded DPIA status directly.'
  ),
  'dpo-designated': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded DPO designation directly.'
  ),
  'consent-purposes-defined': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads recorded consent purposes directly.'
  ),
  'dsr-no-overdue-requests': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Overdue status derived from received date against the response deadline.'
  ),
  // Vulnerability management
  'defender-feed-fresh': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Freshness derived from feed sync time against a window; unprovable until the feed is connected.'
  ),
  'vulnerability-priority-controlled': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Derived from correlated vulnerability tiers against a backlog budget.'
  ),
  // Training
  'training-completion-recorded': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads verified training-completion records directly.'
  ),
  'training-deadline-overdue': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Overdue status derived from assignment deadlines against now.'
  ),
  'uar-campaign-completed': c(
    EVIDENCE_STATE.OBSERVED,
    CONFIDENCE.HIGH,
    'Reads closed UAR campaigns with verified report evidence directly.'
  ),
  // Integration-verified obligations — boolean derived from a synced audit.
  'integration-check': c(
    EVIDENCE_STATE.INFERRED,
    CONFIDENCE.MEDIUM,
    'Boolean derived from a synced integration audit; unprovable until the integration is connected.'
  ),
});

const CHECKER_DEFAULT = c(
  EVIDENCE_STATE.INFERRED,
  CONFIDENCE.MEDIUM,
  'Unclassified checker — treated as inferred pending explicit classification.'
);

// A result is unprovable when the data source behind it is not connected:
//   • a derivation returned the tri-state pass:null, or
//   • a checker reported its integration/feed disconnected via details.
function isUnprovable(result) {
  if (!result) return false;
  if (result.pass === null) return true;
  const d = result.details || {};
  return d.connected === false || d.feedConnected === false;
}

function unprovable(base, result) {
  const notRecorded = (result?.details || {}).notRecorded === true;
  return c(base.state, CONFIDENCE.LOW, notRecorded ? NOT_RECORDED_REASON : UNPROVABLE_REASON);
}

/**
 * Classify an A1 evidence node by its `kind`.
 *
 * @param {{kind: string}} node  a node produced by selectEvidenceNodes()
 * @returns {{state: string, confidence: string, confidenceReason: string}}
 */
export function classifyNode(node) {
  return NODE_KIND_CLASSIFICATION[node?.kind] || NODE_DEFAULT;
}

/**
 * Classify an evidenceDerivations.js derivation. A pass:null result is promoted
 * to the unprovable hedge (low + "unprovable — integration not connected"),
 * keeping the entry's underlying state so the narrative still knows what KIND of
 * proof it would have been.
 *
 * @param {string} derivationKey
 * @param {{pass: boolean|null, detail?: string}} [result]  evaluateDerivation() output
 * @returns {{state: string, confidence: string, confidenceReason: string}}
 */
export function classifyDerivation(derivationKey, result) {
  const base = DERIVATION_CLASSIFICATION[derivationKey] || DERIVATION_DEFAULT;
  return isUnprovable(result) ? unprovable(base, result) : base;
}

/**
 * Classify an autoEvidence.js checker. When the checker's result signals its
 * integration is not connected, it is promoted to the unprovable hedge — the
 * disconnected state must never read as a silent pass.
 *
 * @param {string} checkerType
 * @param {{pass: boolean, summary?: string, details?: object}} [result]  checker output
 * @returns {{state: string, confidence: string, confidenceReason: string}}
 */
export function classifyChecker(checkerType, result) {
  const base = CHECKER_CLASSIFICATION[checkerType] || CHECKER_DEFAULT;
  return isUnprovable(result) ? unprovable(base, result) : base;
}
